// Diego, 13-oct (junta, segunda tanda): «para revisar los recibos y consumos con la tabla deberías ponerle una LUPA con
// zoom al recibo cargado, ya que no se alcanzan a ver los números». Visor con zoom para fotos y hojas de PDF ya dibujadas:
// botones − / + / Ajustar, pellizcar en el celular, arrastrar para moverse, rueda (con Ctrl o pellizco del trackpad) para
// acercar, doble clic / doble toque para acercar ahí, una LUPA que sigue al dedo o al cursor, y pantalla completa.
// Sin dependencias: transform sobre un lienzo con las imágenes apiladas.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as EventoPuntero } from 'react';
import { Maximize, Minimize, Minus, Plus, Search } from 'lucide-react';

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 8;
const PASO = 1.5;
/** Cuánto agranda la lupa lo que hay debajo (sobre lo que ya se ve). */
const AUMENTO_LUPA = 3;

interface Vista {
  s: number;
  x: number;
  y: number;
}

const acotarNum = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export function VisorZoom({ hojas, alt }: { hojas: readonly string[]; alt: string }) {
  const caja = useRef<HTMLDivElement>(null);
  const marco = useRef<HTMLDivElement>(null);
  const lienzo = useRef<HTMLDivElement>(null);
  const [vista, setVista] = useState<Vista>({ s: 1, x: 0, y: 0 });
  const actual = useRef(vista);
  const [lupaActiva, setLupaActiva] = useState(false);
  const [lupa, setLupa] = useState<{ x: number; y: number; src: string; bw: number; bh: number; bx: number; by: number } | null>(null);
  const [completo, setCompleto] = useState(false);
  const punteros = useRef(new Map<number, { x: number; y: number }>());
  const pellizco = useRef<{ dist: number; s: number; px: number; py: number; x: number; y: number } | null>(null);
  const ultimoToque = useRef<{ t: number; x: number; y: number } | null>(null);

  /** Mantiene el recibo dentro del marco: si cabe, centrado a lo ancho y arriba; si no, sin dejar huecos. */
  const acotar = useCallback((v: Vista): Vista => {
    const m = marco.current;
    const l = lienzo.current;
    const s = acotarNum(v.s, ZOOM_MIN, ZOOM_MAX);
    if (!m || !l) return { ...v, s };
    const vw = m.clientWidth;
    const vh = m.clientHeight;
    const cw = l.offsetWidth * s;
    const ch = l.offsetHeight * s;
    const x = cw <= vw ? (vw - cw) / 2 : acotarNum(v.x, vw - cw, 0);
    const y = ch <= vh ? (s < 1 ? (vh - ch) / 2 : 0) : acotarNum(v.y, vh - ch, 0);
    return { s, x, y };
  }, []);

  const poner = useCallback(
    (v: Vista) => {
      const n = acotar(v);
      actual.current = n;
      setVista(n);
      return n;
    },
    [acotar],
  );

  /** Acerca o aleja dejando quieto el punto (px, py) del marco. */
  const zoomEn = useCallback(
    (s: number, px: number, py: number) => {
      const v = actual.current;
      const ns = acotarNum(s, ZOOM_MIN, ZOOM_MAX);
      poner({ s: ns, x: px - ((px - v.x) * ns) / v.s, y: py - ((py - v.y) * ns) / v.s });
    },
    [poner],
  );
  const zoomCentro = (factor: number) => {
    const m = marco.current;
    zoomEn(actual.current.s * factor, (m?.clientWidth ?? 0) / 2, (m?.clientHeight ?? 0) / 2);
  };
  const ajustar = useCallback(() => poner({ s: 1, x: 0, y: 0 }), [poner]);

  // Hojas nuevas (otro recibo) o cambio de tamaño del marco (pantalla completa, girar el teléfono): volver a acomodar.
  useEffect(() => {
    ajustar();
  }, [hojas, ajustar]);
  useEffect(() => {
    const m = marco.current;
    if (!m || typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver(() => poner(actual.current));
    o.observe(m);
    if (lienzo.current) o.observe(lienzo.current);
    return () => o.disconnect();
  }, [poner]);

  // Rueda: con Ctrl/⌘ (o el pellizco del trackpad, que llega como Ctrl+rueda) acerca; sin Ctrl se mueve por el recibo y,
  // al llegar a la orilla, deja que la página siga bajando.
  useEffect(() => {
    const m = marco.current;
    if (!m) return;
    const alRodar = (e: WheelEvent) => {
      const r = m.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomEn(actual.current.s * Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
        return;
      }
      const v = actual.current;
      const n = acotar({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY });
      if (Math.abs(n.x - v.x) < 0.5 && Math.abs(n.y - v.y) < 0.5) return;
      e.preventDefault();
      actual.current = n;
      setVista(n);
    };
    m.addEventListener('wheel', alRodar, { passive: false });
    return () => m.removeEventListener('wheel', alRodar);
  }, [acotar, zoomEn]);

  // Pantalla completa: la del navegador si la hay (computadora, Android); en el iPhone, el visor cubre la pantalla.
  useEffect(() => {
    const alCambiar = () => setCompleto(document.fullscreenElement === caja.current);
    document.addEventListener('fullscreenchange', alCambiar);
    return () => document.removeEventListener('fullscreenchange', alCambiar);
  }, []);
  useEffect(() => {
    if (!completo) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) {
        e.preventDefault();
        e.stopPropagation();
        setCompleto(false);
      }
    };
    window.addEventListener('keydown', alTeclear, true);
    return () => window.removeEventListener('keydown', alTeclear, true);
  }, [completo]);
  const alternarCompleto = () => {
    const c = caja.current;
    if (!c) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    if (!completo && document.fullscreenEnabled && typeof c.requestFullscreen === 'function') {
      c.requestFullscreen().catch(() => setCompleto(true));
      return;
    }
    setCompleto(!completo);
  };

  const moverLupa = (e: EventoPuntero<HTMLDivElement>) => {
    const m = marco.current;
    if (!m) return;
    const debajo = document.elementFromPoint(e.clientX, e.clientY);
    if (!(debajo instanceof HTMLImageElement) || !lienzo.current?.contains(debajo)) {
      setLupa(null);
      return;
    }
    const ri = debajo.getBoundingClientRect();
    const rm = m.getBoundingClientRect();
    const lado = Math.min(rm.width, rm.height) * 0.45;
    const bw = ri.width * AUMENTO_LUPA;
    const bh = ri.height * AUMENTO_LUPA;
    const fx = (e.clientX - ri.left) / ri.width;
    const fy = (e.clientY - ri.top) / ri.height;
    // Con el dedo, la lupa va arriba del dedo para que no la tape.
    const arriba = e.pointerType === 'touch' ? lado * 0.75 : 0;
    setLupa({ x: e.clientX - rm.left, y: e.clientY - rm.top - arriba, src: debajo.currentSrc || debajo.src, bw, bh, bx: lado / 2 - fx * bw, by: lado / 2 - fy * bh });
  };

  const alBajar = (e: EventoPuntero<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (punteros.current.size === 2) {
      const [a, b] = [...punteros.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const r = marco.current!.getBoundingClientRect();
      const v = actual.current;
      pellizco.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: v.s, px: (a.x + b.x) / 2 - r.left, py: (a.y + b.y) / 2 - r.top, x: v.x, y: v.y };
      setLupa(null);
      return;
    }
    if (lupaActiva) moverLupa(e);
    // Doble toque (el doble clic del ratón lo atiende onDoubleClick).
    if (e.pointerType === 'touch' && !lupaActiva) {
      const ahora = Date.now();
      const u = ultimoToque.current;
      if (u && ahora - u.t < 300 && Math.hypot(u.x - e.clientX, u.y - e.clientY) < 30) {
        ultimoToque.current = null;
        dobleEn(e.clientX, e.clientY);
      } else ultimoToque.current = { t: ahora, x: e.clientX, y: e.clientY };
    }
  };
  const alMover = (e: EventoPuntero<HTMLDivElement>) => {
    const antes = punteros.current.get(e.pointerId);
    if (!antes) {
      if (lupaActiva && e.pointerType === 'mouse') moverLupa(e);
      return;
    }
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = pellizco.current;
    if (p && punteros.current.size >= 2) {
      const [a, b] = [...punteros.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const r = marco.current!.getBoundingClientRect();
      const ns = acotarNum((p.s * Math.hypot(a.x - b.x, a.y - b.y)) / p.dist, ZOOM_MIN, ZOOM_MAX);
      const mx = (a.x + b.x) / 2 - r.left;
      const my = (a.y + b.y) / 2 - r.top;
      // El punto del recibo que estaba bajo los dedos sigue bajo los dedos (aunque se muevan).
      poner({ s: ns, x: mx - ((p.px - p.x) * ns) / p.s, y: my - ((p.py - p.y) * ns) / p.s });
      return;
    }
    if (lupaActiva) {
      moverLupa(e);
      return;
    }
    const v = actual.current;
    poner({ ...v, x: v.x + e.clientX - antes.x, y: v.y + e.clientY - antes.y });
  };
  const alSoltar = (e: EventoPuntero<HTMLDivElement>) => {
    punteros.current.delete(e.pointerId);
    if (punteros.current.size < 2) pellizco.current = null;
    if (e.pointerType !== 'mouse') setLupa(null);
  };
  const dobleEn = (cx: number, cy: number) => {
    const r = marco.current?.getBoundingClientRect();
    if (!r) return;
    if (actual.current.s > 1.2) ajustar();
    else zoomEn(3, cx - r.left, cy - r.top);
  };

  const pct = Math.round(vista.s * 100);
  const lado = lupa && marco.current ? Math.min(marco.current.clientWidth, marco.current.clientHeight) * 0.45 : 0;
  return (
    <div ref={caja} className={['visor', 'visor--zoom', completo ? 'visor--completo' : ''].join(' ')}>
      <div
        ref={marco}
        className={['visor__marco', lupaActiva ? 'visor__marco--lupa' : vista.s > 1.01 ? 'visor__marco--mueve' : ''].join(' ')}
        onPointerDown={alBajar}
        onPointerMove={alMover}
        onPointerUp={alSoltar}
        onPointerCancel={alSoltar}
        onPointerLeave={() => setLupa(null)}
        onDoubleClick={(e) => !lupaActiva && dobleEn(e.clientX, e.clientY)}
        tabIndex={0}
        role="img"
        aria-label={`${alt}. Acerca con + o pellizcando y arrastra para moverte.`}
        onKeyDown={(e) => {
          if (e.key === '+' || e.key === '=') zoomCentro(PASO);
          else if (e.key === '-') zoomCentro(1 / PASO);
          else if (e.key === '0') ajustar();
          else return;
          e.preventDefault();
        }}
      >
        <div ref={lienzo} className="visor__lienzo" style={{ transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.s})` }}>
          {hojas.map((src, i) => (
            <img key={src} className="visor__hoja" src={src} alt={hojas.length > 1 ? `${alt}, hoja ${i + 1}` : alt} draggable={false} onLoad={() => poner(actual.current)} />
          ))}
        </div>
        {lupa && (
          <div
            className="visor__lupa"
            aria-hidden
            style={{
              width: lado,
              height: lado,
              left: lupa.x - lado / 2,
              top: lupa.y - lado / 2,
              backgroundImage: `url("${lupa.src}")`,
              backgroundSize: `${lupa.bw}px ${lupa.bh}px`,
              backgroundPosition: `${lupa.bx}px ${lupa.by}px`,
            }}
          />
        )}
      </div>
      <div className="visor__barra" role="toolbar" aria-label="Zoom del recibo">
        <button type="button" className="visor__boton" aria-label="Alejar" onClick={() => zoomCentro(1 / PASO)} disabled={vista.s <= ZOOM_MIN + 0.001}>
          <Minus aria-hidden />
        </button>
        <span className="visor__pct" aria-live="polite">
          {pct} %
        </span>
        <button type="button" className="visor__boton" aria-label="Acercar" onClick={() => zoomCentro(PASO)} disabled={vista.s >= ZOOM_MAX - 0.001}>
          <Plus aria-hidden />
        </button>
        <button type="button" className="visor__boton visor__boton--texto" aria-label="Ajustar al ancho" title="Ajustar al ancho" onClick={ajustar}>
          Ajustar
        </button>
        <button
          type="button"
          className={['visor__boton', lupaActiva ? 'es-activo' : ''].join(' ')}
          aria-pressed={lupaActiva}
          aria-label="Lupa"
          title="Lupa: pasa el cursor o el dedo sobre el recibo"
          onClick={() => {
            setLupaActiva(!lupaActiva);
            setLupa(null);
          }}
        >
          <Search aria-hidden />
        </button>
        <button type="button" className="visor__boton" aria-label={completo ? 'Salir de pantalla completa' : 'Pantalla completa'} title={completo ? 'Salir de pantalla completa' : 'Pantalla completa'} onClick={alternarCompleto}>
          {completo ? <Minimize aria-hidden /> : <Maximize aria-hidden />}
        </button>
      </div>
    </div>
  );
}
